import { MoreHorizontal } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PinMark } from '../../components/PinMark';
import { DAY_NAMES, DAY_NAMES_LONG } from './dayNames';

type DayHeaderProps = {
  weekday: number;
  /** Day of the month; absent on the default timetable. */
  dayNumber?: number | undefined;
  today?: boolean;
  /** The day view; the heading links there. */
  href?: string;
  onCopyTo?: (toWeekday: number) => void;
  onClear?: () => void;
};

/** The head of a day column: name, date, the pin on today, and the day menu on the editable sheet. */
export function DayHeader({ weekday, dayNumber, today, href, onCopyTo, onClear }: DayHeaderProps) {
  const name = DAY_NAMES[weekday - 1] ?? '';
  const heading = (
    <>
      {dayNumber !== undefined && <span className="day-number">{dayNumber}</span>}
      <span className="font-semibold">{name}</span>
      {today && <span className="sr-only"> (vandaag)</span>}
    </>
  );
  return (
    <div role="columnheader" aria-current={today ? 'date' : undefined} className="relative flex items-baseline gap-2 px-2 pb-1 pt-2">
      {today && <PinMark size={16} className="absolute -top-1 left-0" />}
      {href ? (
        <Link to={href} className="flex items-baseline gap-2 hover:underline">
          {heading}
        </Link>
      ) : (
        <span className="flex items-baseline gap-2">{heading}</span>
      )}
      {(onCopyTo || onClear) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Menu ${name}`} className="no-print ml-auto min-h-11 min-w-11 self-center">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onCopyTo && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="min-h-11">Kopieer naar…</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {DAY_NAMES_LONG.map((long, index) =>
                    index + 1 === weekday ? null : (
                      <DropdownMenuItem key={long} onSelect={() => onCopyTo(index + 1)} className="min-h-11">
                        {long}
                      </DropdownMenuItem>
                    ),
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {onClear && (
              <DropdownMenuItem onSelect={onClear} className="min-h-11">
                Maak leeg
              </DropdownMenuItem>
            )}
            {href && (
              <DropdownMenuItem asChild className="min-h-11">
                <Link to={href}>Zoom in</Link>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
